<template>
  <!--
  <div class="switch-tabs-container">
    <button
        class="tab-button tab-button--first"
        :class="{'tab-button-active': activeTab === 'night'}"
        tabindex="0"
        role="tab"
        aria-selected="true"
        @click="handleTabChange('night')"
        @keydown.enter="handleTabChange('night')"
    >
      Per night
    </button>
    <button
        class="tab-button tab-button--second"
        :class="{'tab-button-active': activeTab === 'stay'}"
        tabindex="0"
        role="tab"
        aria-selected="false"
        @click="handleTabChange('stay')"
        @keydown.enter="handleTabChange('stay')"
    >
      Per stay
    </button>
  </div>
  -->
  <div class="range-inputs">
    <div class="input-wrapper">
      <label for="min-price" class="input-label">{{t('Min')}}</label>
      <input v-model="displayMinPrice" type="text" id="min-price" class="price-input" @change="changePriceRange"/>
    </div>
    <span class="input-separator"></span>
    <div class="input-wrapper">
      <label for="max-price" class="input-label">{{t('Max')}}</label>
      <input v-model="displayMaxPrice" type="text" id="max-price" class="price-input"  @change="changePriceRange"/>
    </div>
  </div>
  <div class="range-slider">
    <Slider
        v-model="priceRange"
        range
        :min="defaultPriceMin || 0"
        :max="defaultPriceMax || 2000"
        class="full-width"
        @change="changePriceRange"/>
  </div>
</template>

<script setup lang="ts">
import Slider from "primevue/slider";
import {useRoute} from "vue-router";
import { useSearchStore} from "~/store/search";
import { useAccountStore } from "~/store/account";
import { watch } from 'vue';
const searchStore = useSearchStore();
const accountStore = useAccountStore();
const currency = computed(() => accountStore.currency);
const route = useRoute();
const { t } = useI18n()
const nights = computed(() => {
  const { date_start, date_end } = route.query
  return countNights(date_start as string, date_end as string)
});

const divider = computed(() => (activeTab.value === 'night' ? nights.value || 1 : 1));
//how to switch between per stay and per night prices?

const timeout = ref<any>(null)
const emit = defineEmits(['update']);




const defaultPriceMin = computed({
  get: () => parseFloat(searchStore.filters?.price_min?.amount) || 0,
  set: (val: number) => {
    searchStore.filters.price_min.amount = val.toString();
  }
});

const defaultPriceMax = computed({
  get: () => parseFloat(searchStore.filters?.price_max?.amount) || 2000,
  set: (val: number) => {
    searchStore.filters.price_max.amount = val.toString();
  }
});

const priceRange = ref([defaultPriceMin.value || 0, defaultPriceMax.value || 2000]);

const minPrice = computed({
  get: () => priceRange.value[0] || defaultPriceMin.value,
  set: (val) => {
    priceRange.value[0] = val;
  },
});

const displayMinPrice = computed({
  get: () => (minPrice.value !== null ? `${currency.value.symbol}${minPrice.value}` : ''),
  set: (val: string) => {
    const numericValue = parseFloat(val.replace(/[^0-9.]/g, ''));
    minPrice.value = isNaN(numericValue) ? 0 : numericValue;
  }
});

const maxPrice = computed({
  get: () => priceRange.value[1] || defaultPriceMax.value,
  set: (val) => {
    priceRange.value[1] = val;
  },
});


const displayMaxPrice = computed({
  get: () => (maxPrice.value !== null ? `${currency.value.symbol}${maxPrice.value}` : ''),
  set: (val: string) => {
    const numericValue = parseFloat(val.replace(/[^0-9.]/g, ''));
    maxPrice.value = isNaN(numericValue) ? 2000 : numericValue;
  }
});


onMounted(() => {
  const { price_gte:price_min, price_lte:price_max } = route.query;

  if (price_min && price_max) {
    priceRange.value = [+price_min, +price_max];
    displayMaxPrice.value = `${currency.value.symbol}${maxPrice.value}`;
    displayMinPrice.value = `${currency.value.symbol}${minPrice.value}`;
  }
});

interface TabChangeEvent {
  (type: 'night' | 'stay'): void
}

const activeTab = ref<'night' | 'stay'>('stay')



const handleTabChange: TabChangeEvent = (type) => {
  if (activeTab.value === type) return;
  activeTab.value = type;
  const [min, max] = priceRange.value;


  if (priceRange.value) {
    const finalMin = activeTab.value === 'stay' ? (min ?? 0) * nights.value : Math.round((min ?? 0) / divider.value);
    const finalMax = activeTab.value === 'stay' ? (max ?? 2000) * nights.value : Math.round((max ?? 2000) / divider.value);

    updatePriceFilter([finalMin, finalMax]);
  }
}

function changePriceRange() {
  clearTimeout(timeout.value);
  timeout.value = setTimeout(() => {
    const [min, max] = priceRange.value;
    const finalMin = (min ?? 0) * divider.value;
    const finalMax = (max ?? 2000) * divider.value;
    emit('update', [finalMin, finalMax]);
  }, 500)
}

function countNights(dateStart: string, dateEnd: string): number {
  const startDate = new Date(dateStart);
  const endDate = new Date(dateEnd);
  const timeDifference = endDate.getTime() - startDate.getTime();
  const nights = timeDifference / (1000 * 3600 * 24);
  return nights > 0 ? nights : 1;
}

function updatePriceFilter([min, max]: [number, number]) {
  minPrice.value = min;
  maxPrice.value = max;
  displayMinPrice.value = `${currency.value.symbol}${min}`;
  displayMaxPrice.value = `${currency.value.symbol}${max}`;

  priceRange.value = [minPrice.value,  maxPrice.value];
}

watch(
  () => [route.query.price_gte, route.query.price_lte],
  ([priceGte, priceLte]) => {
    const sliderMin = defaultPriceMin.value || 0;
    const sliderMax = defaultPriceMax.value || 2000;

    if (!priceGte && !priceLte) {
      priceRange.value = [sliderMin, sliderMax];
      return;
    }

    const normalizedMin = priceGte ? Number(priceGte) / (divider.value || 1) : sliderMin;
    const normalizedMax = priceLte ? Number(priceLte) / (divider.value || 1) : sliderMax;

    priceRange.value = [normalizedMin, normalizedMax];
  },
  { immediate: true }
);
</script>



<style scoped lang="scss">
// Color Variables
$grey-secondary: var(--light-grey-border);
$black: var(--black);
$light-grey: var(--light-grey);
$primary-blue: var(--blue);
$white: var(--white);
$dark-grey: var(--dark-grey);

// Border Radius Variables
$border-radius-sm: 8px;
$border-radius-md: 10px;
$border-radius-lg: 25px;
// Font Variables
$font-family-base: 'Albert Sans', sans-serif;
$font-weight-medium: 500;
$font-weight-semibold: 600;
$font-size-xs: 12px;
$font-size-sm: 14px;
$font-size-base: 16px;
$font-size-lg: 20px;

// Spacing Variables
$spacing-xs: 4px;
$spacing-md: 8px;
$spacing-lg: 16px;
$spacing-xl: 20px;
$spacing-xxl: 24px;
// Mixins
@mixin flex-center {
  display: flex;
  align-items: center;
}

.range-inputs {
  @include flex-center;
  margin-top: $spacing-lg;
  width: 100%;
  gap: 12px;
  font-family: $font-family-base;

  .input-wrapper {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 4px;

    .input-label {
      display: block;
      font-size: $font-size-xs;
      color: var(--dark-grey);
      font-weight: 500;
      margin-bottom: 4px;
      line-height: 1.2;
    }

    .price-input {
      width: 100%;
      padding: 12px;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
      font-size: $font-size-sm;
      color: $black;
      background: var(--white);
      transition: all 0.2s ease;
      text-align: center;

      &:focus {
        outline: none;
        border-color: #1985F9;
        box-shadow: 0 0 0 3px rgba(25, 133, 249, 0.1);
      }
    }
  }

  .input-separator {
    width: 12px;
    height: 1px;
    background-color: #e0e0e0;
    margin: 0 4px;
    margin-top: 20px;
  }
}

.range-slider {
  @include flex-center;
  width: 100%;
  margin: 16px 0 8px 0;

  .full-width {
    width: 100%;
  }
}


.switch-tabs-container {
  align-items: center;
  align-self: stretch;
  border-radius: 25px;
  background: var(--light-grey);
  display: flex;
  justify-content: flex-start;
  font: 14px/1 Albert Sans, sans-serif;
  position: relative;
}

.tab-button {
  align-self: stretch;
  border-radius: 25px;
  height: 32px;
  border: none;
  flex: 1;
  margin: 0;
  padding: 6px 0;
  font-weight: 500;
  cursor: pointer;
  background: transparent;
  color: var(--black);
  transition: all 0.7s ease;
  position: relative;
  z-index: 1;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    border-radius: 25px;
    z-index: -1;
    transition: transform 0.7s ease;
  }
}


.tab-button-active {
  color: var(--white);
  font-weight: 600;
}


.tab-button--first::before {
  background: var(--blue);
}

.tab-button--first.tab-button-active {
  &::before {
    transform: translateX(0);
  }
}

.tab-button--first:not(.tab-button-active) {
  &::before {
    transform: translateX(100%);
  }
}
</style>
